// src/components/auth/Register.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.displayName || !formData.email || !formData.password || !formData.userType) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Register user
      await register(
        formData.email, 
        formData.password, 
        formData.userType, 
        formData.displayName
      );
      
      // Redirect to appropriate dashboard based on user type
      switch (formData.userType) {
        case 'parents':
          navigate('/parent/profile');
          break;
        case 'providers':
          navigate('/provider/profile');
          break;
        case 'jobseekers':
          navigate('/jobseeker/profile');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Sign in with Google
      const result = await loginWithGoogle();
      
      if (result.isNewUser) {
        // If new user, navigate to user type selection
        navigate('/auth/select-user-type', { 
          state: { uid: result.user.uid } 
        });
      } else {
        // If existing user, navigate to appropriate dashboard
        navigate('/');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Create Account</h1>
          <p>Join TotsPal today</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="displayName">Full Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled={loading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              disabled={loading}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                disabled={loading}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                disabled={loading}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>I am a:</label>
            <div className="user-type-options">
              <label className={`user-type-option${formData.userType === 'parents' ? ' selected' : ''}`}> 
                <input
                  type="radio"
                  name="userType"
                  value="parents"
                  checked={formData.userType === 'parents'}
                  onChange={handleChange}
                  required
                />
                <span>Parent</span>
              </label>
              <label className={`user-type-option${formData.userType === 'providers' ? ' selected' : ''}`}> 
                <input
                  type="radio"
                  name="userType"
                  value="providers"
                  checked={formData.userType === 'providers'}
                  onChange={handleChange}
                  required
                />
                <span>Childcare Provider</span>
              </label>
              <label className={`user-type-option${formData.userType === 'jobseekers' ? ' selected' : ''}`}> 
                <input
                  type="radio"
                  name="userType"
                  value="jobseekers"
                  checked={formData.userType === 'jobseekers'}
                  onChange={handleChange}
                  required
                />
                <span>Job Seeker (Nanny/ECE)</span>
              </label>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <button 
          type="button" 
          className="google-button"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <div className="google-button-content">
            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            <span>Sign up with Google</span>
          </div>
        </button>
        
        <div className="auth-links">
          <p>
            Already have an account? <Link to="/auth/login">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;